import React, { Component } from 'react'
import { getUserSettings, sendFingerprint } from './api'
import {
  createHashRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  defer,
} from 'react-router-dom'
import Intercom from '@intercom/messenger-js-sdk';
import LogRocket from 'logrocket';
import mixpanel from 'mixpanel-browser';

import './scss/style.scss'
import { CreditsProvider } from './contexts/CreditsContext'

// Helper function to load remote script
const loadRemoteScript = (url) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

// Containers
const AuthLayout = React.lazy(() => import('./layout/AuthLayout'))
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))

const handleStripeRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const setupIntent = urlParams.get('setup_intent');
  const redirectStatus = urlParams.get('redirect_status');
  
  if (setupIntent && redirectStatus === 'succeeded') {
    const redirectPath = '/#/billing/payments';
    window.history.replaceState({}, '', window.location.origin + redirectPath);
    return true;
  }
  return false;
}

const getUserData = () =>
  new Promise((resolve) => {
    console.log('Starting getUserData with KittAI SDK...');
    
    // Check if superUserLogin query parameter is present
    const urlParams = new URLSearchParams(window.location.search);
    const isSuperUserLogin = urlParams.has('superUserLogin');
    
    if (isSuperUserLogin) {
      console.log('Super user login detected - skipping fingerprint and Intercom initialization');
    }
    
    const startTime = performance.now();
    
    // Build promises array conditionally
    const promises = [
      getUserSettings().catch(error => {
        console.error('getUserSettings failed:', error);
        throw error;
      })
    ];
    
    // Only add KittAI fingerprint if not super user login
    if (!isSuperUserLogin) {
      promises.push(
        // Load and initialize KittAI SDK
        (async () => {
          try {
            await loadRemoteScript(process.env.REACT_APP_API_BASE_ENDPOINT + '/js/form_blocker?t=eyJpdiI6Ik02VkJrMmRuczk5eE1jMVF4dFcrNFE9PSIsImRhdGEiOiJkTENMKzVzYXNDUUZNTlZJTThTeE1RPT0ifQ%3D%3D&invalidURL=&validateEmail=false&treatCatchallAsValid=true&treatFakeWebsitesAsInvalid=true&treatFreeEmailProviderAsInvalid=true&treatDisposableEmailsAsInvalid=true&treatAliasesAsInvalid=true&timeout=10&customId=&blockNetworkTampering=false&blockRiskyIPs=true&blockMobileSignup=true&blockNonChromeSignup=true&blockBots=true&blockHighRiskRegions=false');
            
            return new Promise((resolveKittAI) => {
              // Register the event listener
              window.KittAI.on('onFormTokenReceived', function(data) {
                console.log('Form token data received:', data);
                resolveKittAI({ success: true, data });
              });
              
              // Initialize KittAI
              window.KittAI.init();
            });
          } catch (error) {
            console.error('KittAI initialization failed:', error);
            return { success: false, error: error.message };
          }
        })()
      );
    }
    
    Promise.all(promises)
      .then((results) => {
        const settingsResponse = results[0];
        const kittAIResult = !isSuperUserLogin ? results[1] : null;
        
        const totalTime = performance.now() - startTime;
        
        console.log('Service initialization completed:', {
          settingsResponse: !!settingsResponse,
          kittAI: kittAIResult,
          isSuperUserLogin,
          totalTimeMs: totalTime.toFixed(2)
        });
        
        resolve(true)
        
        window.stripePk = settingsResponse.data.stripePublicAPIKey
        
        // Send fingerprint data to server if KittAI was successful and not super user
        if (!isSuperUserLogin && kittAIResult && kittAIResult.success) {
          sendFingerprint(kittAIResult.data).catch(error => 
            console.error('Failed to send fingerprint:', error)
          );
        } else if (!isSuperUserLogin) {
          console.warn('KittAI failed - skipping fingerprint submission');
        }
        
        // Handle Stripe redirect if necessary
        handleStripeRedirect();

/*        LogRocket.init('jw1x7p/kitt-ai');
        LogRocket.identify(settingsResponse.data.userId, {
          name: settingsResponse.data.firstName + ' ' + settingsResponse.data.lastName,
          email: settingsResponse.data.userEmail,
          subscriptionType: 'pro'
        });
*/        
        // Only initialize Intercom if not super user login
        if (!isSuperUserLogin) {
          Intercom({
            app_id: 'yav2ecqa',
            user_id: settingsResponse.data.userEmail,
            name: settingsResponse.data.firstName + ' ' + settingsResponse.data.lastName,
            email: settingsResponse.data.userEmail,
            created_at: settingsResponse.data.createdDateEpoch,
          });
        }

        // Initialize Mixpanel
        mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN || 'd3fae8a79e1fc2c3fda68045341857d4', {
          debug: process.env.NODE_ENV === 'development',
          track_pageview: false,
          persistence: 'localStorage'
        });

        // Identify user in Mixpanel
        mixpanel.identify(settingsResponse.data.userEmail);
        mixpanel.people.set({
          $name: settingsResponse.data.firstName + ' ' + settingsResponse.data.lastName,
          $email: settingsResponse.data.userEmail,
          subscriptionType: 'pro',
          createdDate: settingsResponse.data.createdDateEpoch,
          userId: settingsResponse.data.userId
        });

        // Track page view on app load
        mixpanel.track('Page View', {
          page: window.location.pathname + window.location.hash,
          title: document.title,
          url: window.location.href,
          referrer: document.referrer,
          kittAISuccess: kittAIResult ? kittAIResult.success : false,
          isSuperUserLogin
        });
      })
      .catch((error) => {
        const totalTime = performance.now() - startTime;
        console.error('getUserData failed:', { error: error.message, totalTimeMs: totalTime.toFixed(2) });
        resolve(false)
      })
  })

const router = createHashRouter(
  createRoutesFromElements(
    <Route element={<AuthLayout />} loader={() => defer({ authPromise: getUserData() })}>
      <Route exact path="/login" name="Login Page" element={<Login />} />
      <Route exact path="/404" name="Page 404" element={<Page404 />} />
      <Route path="*" name="Home" element={<DefaultLayout />} />
    </Route>,
  ),
)

class App extends Component {
  constructor(props) {
    super(props);
    this.handleInitialRedirect();
  }

  handleInitialRedirect = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const setupIntent = urlParams.get('setup_intent');
    const redirectStatus = urlParams.get('redirect_status');
    
    if (setupIntent && redirectStatus === 'succeeded') {
      window.location.href = window.location.origin + '/#/billing/payments';
      window.history.replaceState({}, '', window.location.origin + '/#/billing/payments');
    }
  }

  render() {
    return (
      <React.StrictMode>
        <RouterProvider router={router} />
      </React.StrictMode>
    )
  }
}

export default App
